import { useAuthStore } from '@/entities/auth';
import { getTerminalId } from '@/shared/bridge';
import { isVersionBlocked } from '@/shared/version';

export interface FcmSyncDeps {
  /** `POST /api/v3/auth/fcm` — 디바이스 토큰 등록(인증 필요). */
  registerFcm: (body: { terminalId: string; fcmToken: string }) => Promise<unknown>;
}

export interface FcmSync {
  /** FCM 디바이스 토큰 발급/갱신 시 (native `push.tokenIssued` / web FCM JS SDK). */
  setFcmToken: (fcmToken: string) => void;
  /** access token 이 생겼을 때 (로그인 / 세션 복원). */
  onAuthenticated: () => void;
  /** 로그아웃/세션 클리어 — 다음 로그인에서 재등록되도록 상태 리셋. */
  reset: () => void;
}

/**
 * FCM 토큰 → BE 등록의 **단일 코디네이터**. 두 트리거를 한 곳에서 합류시킨다:
 *   ① FCM 토큰 변경 (`setFcmToken`, native push.tokenIssued / onTokenRefresh)
 *   ② 인증 성립 (`onAuthenticated`, access token 생성)
 * 둘 중 뭐가 먼저 와도 **fcmToken && accessToken** 이 모두 갖춰진 순간 1회 POST.
 *
 * ⚠️ 이 파일엔 성격이 다른 토큰이 둘 섞인다 — **`fcmToken`**(등록 대상, 푸시 디바이스 토큰)과
 * **`accessToken`**(인증 수단). 변수명에 그냥 `token` 을 쓰지 말 것(어느 쪽인지 즉시 안 보인다).
 *
 * 로그인 body 에 싣던 방식을 대체 — BE 정책상 등록엔 access token 이 필요하고(전용
 * 엔드포인트), 토큰은 세션 도중에도 갱신될 수 있어 login 시점 한 번으론 부족하다.
 * 중복 전송은 `lastSent` 로 차단(같은 토큰 재전송 안 함). 관련 ADR: push-notifications.
 */
export function createFcmSync(deps: FcmSyncDeps): FcmSync {
  let fcmToken = '';
  // 마지막으로 등록에 성공한(또는 진행 중인) FCM 토큰. 중복 POST 차단 + 실패 시 재시도 여지.
  let lastSentFcmToken: string | null = null;

  function sync(): void {
    // 강제 업데이트 상태면 BE 를 두드리지 않는다 — 구버전의 디바이스 등록은 무의미하고, 401 이면
    // 토큰 재발급까지 연쇄된다. 업데이트 후 재부팅하면 정상 경로로 등록된다.
    if (isVersionBlocked()) return;
    // native(bridge) / web(FCM JS SDK) 양쪽에서 공급됨 — 환경 게이트 없이 fcmToken 유무로 판단.
    if (!fcmToken) return; // 아직 FCM 토큰 미도착(브라우저 push 미설정·권한거부 포함).
    if (!useAuthStore.getState().accessToken) return; // 인증 후에만 등록 가능.
    if (fcmToken === lastSentFcmToken) return; // 이미 등록됨 — 중복 방지.

    const pendingFcmToken = fcmToken;
    lastSentFcmToken = pendingFcmToken; // 낙관적 마킹 — 동시 sync 의 이중 POST 차단.
    void deps.registerFcm({ terminalId: getTerminalId(), fcmToken: pendingFcmToken }).catch((e) => {
      // 실패 시 마킹 되돌려 다음 트리거(재로그인/FCM 토큰 갱신)에서 재시도.
      if (lastSentFcmToken === pendingFcmToken) lastSentFcmToken = null;
      console.warn('[fcm-sync] register failed', e);
    });
  }

  return {
    setFcmToken(next) {
      fcmToken = next;
      sync();
    },
    onAuthenticated() {
      sync();
    },
    reset() {
      lastSentFcmToken = null;
    },
  };
}
